import { useCallback, useEffect, useRef, useState } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { getUserReviewedFields } from "@common/schemas/transactionFieldReview.schema";
import { pluralizeWithCount } from "@common/helpers/string";
import { bulkRevokeFieldReviews } from "@api/transaction.api";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { Button } from "@design-system/Button/Button";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUser } from "@hooks/useUser";
import {
    buildBulkFieldReviewRequests,
    reviewActionDropsFromFilter,
    submitBulkFieldReviewRequests,
} from "../TransactionsBulkActions/buildBulkFieldReviewRequests";
import { TransactionsFilters } from "../filters/lib";
import {
    invalidateTransactionsQueries,
    updateTransactionInCache,
} from "../useTransactionsQuery";

interface UnreviewSelectedButtonProps {
    reviewTransactionFields: TransactionReviewField[];
    selectedTransactions: TransactionListItem[];
    onClearSelection: () => void;
    hasSelectedAll?: boolean;
    totalTransactions?: number;
    filtersSelection?: TransactionsFilters | null;
    workspaceId?: string | null;
    fallbackEntityIds?: number[];
}

export const UnreviewSelectedButton = ({
    reviewTransactionFields,
    selectedTransactions,
    onClearSelection,
    hasSelectedAll = false,
    totalTransactions = 0,
    filtersSelection = null,
    workspaceId,
    fallbackEntityIds = [],
}: UnreviewSelectedButtonProps) => {
    const user = useUser();
    const { toast, error: errorToast } = useToaster();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleUnreview = useCallback(async () => {
        setIsSubmitting(true);
        const revokedFieldSet = new Set(reviewTransactionFields);
        // Count only rows the current user had actually reviewed so the toast
        // reflects what changed, mirroring handleReview's reviewedCount.
        const unreviewedCount = selectedTransactions.filter((transaction) => {
            const reviewedFieldSet = getUserReviewedFields(
                transaction.fieldReviews,
                user.id,
            );
            return reviewTransactionFields.some((f) => reviewedFieldSet.has(f));
        }).length;

        try {
            const requests = buildBulkFieldReviewRequests({
                fields: reviewTransactionFields,
                transactionIds: selectedTransactions.map((t) => t.id),
                filtersSelection: hasSelectedAll ? filtersSelection : null,
                workspaceId,
                fallbackEntityIds,
                transactionCount: hasSelectedAll
                    ? totalTransactions
                    : selectedTransactions.length,
                dropsFromFilter: reviewActionDropsFromFilter(
                    hasSelectedAll ? filtersSelection : null,
                    reviewTransactionFields,
                    "revoke",
                ),
            });
            if (requests.length === 0) {
                return;
            }
            await submitBulkFieldReviewRequests(
                requests,
                bulkRevokeFieldReviews,
            );

            if (!isMountedRef.current) {
                return;
            }

            if (hasSelectedAll) {
                await invalidateTransactionsQueries();
            } else {
                for (const transaction of selectedTransactions) {
                    updateTransactionInCache(transaction.id, (t) => ({
                        ...t,
                        fieldReviews: (t.fieldReviews ?? []).filter(
                            (r) =>
                                !(
                                    r.userId === user.id &&
                                    revokedFieldSet.has(r.field)
                                ),
                        ),
                    }));
                }
            }

            onClearSelection();
            const toastCount = hasSelectedAll
                ? totalTransactions
                : unreviewedCount;
            toast(
                toastCount > 0
                    ? `Unreviewed ${pluralizeWithCount(
                          "transaction",
                          toastCount,
                      )}`
                    : "Selected transactions are not reviewed",
            );
        } catch (error) {
            if (isViewOnlyRefusalError(error)) {
                return;
            }
            if (isMountedRef.current) {
                errorToast(
                    "Failed to unreview transactions. Please try again.",
                );
            }
        } finally {
            if (isMountedRef.current) {
                setIsSubmitting(false);
            }
        }
    }, [
        reviewTransactionFields,
        selectedTransactions,
        onClearSelection,
        hasSelectedAll,
        totalTransactions,
        filtersSelection,
        workspaceId,
        fallbackEntityIds,
        user.id,
        toast,
        errorToast,
    ]);

    return (
        <Button
            variant="primary"
            size="md"
            onClick={handleUnreview}
            disabled={isSubmitting}
        >
            Unreview
        </Button>
    );
};
